import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { DataPage } from '../../models/data-source';
import { Comparison, Interval, Snapshot } from './overview-model';

export interface OverviewPeriod {
  id: string; interval: Interval; periodStart: string; comparison: Comparison; version: number; source: string;
}
export interface OverviewReport extends OverviewPeriod {
  /** Periods available for this report's interval and comparison, newest first. */
  periods: OverviewPeriod[];
  data: { schemaVersion: 1; currency: 'USD'; unit: 'thousands'; snapshot: Snapshot };
  history: { id: string; snapshot: Snapshot }[];
}
export interface SavedAnalysis {
  id: string; reportId: string; conversationId: string | null; revision: number; updatedAt: string;
  /** The report title and its complete Markdown document; markdown is null when the stored content is unreadable. */
  title: string; markdown: string | null; error?: string;
  templateId?: string | null; templateRevision?: number | null;
  /** Present on the single-analysis read only. */
  templateName?: string | null;
}
/** One saved report in Analysis & Reports, with its full Markdown so the list renders without a request per row. */
export interface OverviewAnalysisListItem {
  id: string; reportId: string; title: string; interval: Interval; periodStart: string; periodEnd: string; updatedAt: string;
  revision: number; templateId: string | null; templateName: string | null; markdown: string | null; error?: string | null;
}
/** Filters for the one report read; reportId selects an exact saved version and wins over the period filters. */
export interface OverviewReportQuery { reportId?: string; interval?: Interval; period?: string; comparison?: Comparison }
/** Analysis & Reports filters, order and page; reportId narrows to one financial report. */
export interface OverviewAnalysisQuery {
  offset?: number; pageSize?: number; interval?: Interval; periodStart?: string; keyword?: string; sort?: string; desc?: boolean; reportId?: string; templateId?: string;
}
/** A report template as users see it; the backend keeps the underlying skill private. */
export interface OverviewTemplate {
  id: string; name: string; description: string; revision: number; isActive: boolean; isSystem: boolean; createdAt: string; updatedAt: string;
  /** Reports the caller generated from this template. */
  reportCount: number;
}
export interface OverviewTemplateQuery {
  offset?: number; pageSize?: number; includeInactive?: boolean; search?: string; sort?: string; desc?: boolean;
}
/** A template's editable definition. The instruction also states the requested output (charts, tables). */
export interface OverviewTemplateDetail extends OverviewTemplate { instructions: string }
export interface SaveOverviewTemplate { revision: number; name: string; description: string; instructions: string; isActive: boolean }
@Injectable({ providedIn: 'root' })
export class OverviewApiService {
  constructor(private readonly http: HttpClient) {}
  analysisList(query: OverviewAnalysisQuery = {}) {
    const params = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== '')) as Record<string, string | number | boolean>;
    return this.http.get<{ items: OverviewAnalysisListItem[]; total: number }>('/api/overview/analyses', { params });
  }
  templates(query: OverviewTemplateQuery = {}) {
    const params = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== '')) as Record<string, string | number | boolean>;
    return this.http.get<DataPage<OverviewTemplate>>('/api/overview/templates', { params });
  }
  templateDetail(id: string) { return this.http.get<OverviewTemplateDetail>('/api/overview/templates/' + encodeURIComponent(id)); }
  /** Revision 0 creates under the caller-chosen id; the current revision updates. */
  saveTemplate(id: string, input: SaveOverviewTemplate) {
    return this.http.put<OverviewTemplateDetail>('/api/overview/templates/' + encodeURIComponent(id), input);
  }
  report(query: OverviewReportQuery) {
    const params = Object.fromEntries(Object.entries(query).filter(([, value]) => value)) as Record<string, string>;
    return this.http.get<OverviewReport>('/api/overview/report', { params }).pipe(map(report => {
      if (!report.data || report.data.schemaVersion !== 1 || report.data.currency !== 'USD' || report.data.unit !== 'thousands')
        throw new Error('This financial report format is not supported.');
      const validSnapshot = (snapshot: Snapshot) => !!snapshot?.period?.start && !!snapshot.comparePeriod?.start
        && ['current','compare','ytd'].every(key => {
          const amounts = snapshot[key as 'current' | 'compare' | 'ytd'];
          return amounts && ['revenue','serviceCost','overhead','profit','interest','tax','netIncome','operating','investing','financing','net']
            .every(field => Number.isFinite(amounts[field as keyof typeof amounts]));
        }) && ['balance','compareBalance'].every(key => {
          const balances = snapshot[key as 'balance' | 'compareBalance'];
          return balances && ['cash','receivables','payables','debt'].every(field => Number.isFinite(balances[field as keyof typeof balances]));
        });
      if (!validSnapshot(report.data.snapshot)
          || !Array.isArray(report.history) || !report.history.every(item => validSnapshot(item.snapshot)) || !Array.isArray(report.periods))
        throw new Error('This financial report is incomplete.');
      return report;
    }));
  }
  analysis(id: string) { return this.http.get<SavedAnalysis>('/api/overview/analyses/' + encodeURIComponent(id)); }
  discussion(id: string) { return this.http.get<{ title: string; message: string }>('/api/overview/analyses/' + encodeURIComponent(id) + '/discussion'); }
  remove(panel: SavedAnalysis) {
    return this.http.delete<void>('/api/overview/analyses/' + encodeURIComponent(panel.id), { params: { revision: panel.revision } });
  }
}
